import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DroneInventory {
  id: string;
  name: string;
  manufacturerId: string;
  modelId: string;
  serialNumber: string;
  packagingSerialNumber: string | null;
  version: string | null;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  inventoryNote: string | null;
  // Registration fields
  remoteId: string | null;
  remoteBroadcastModule: string | null;
  nNumber: string | null;
  nNumberActiveDate: string | null;
  // Joined fields
  manufacturerName?: string;
  modelName?: string;
  createdAt: string;
  updatedAt: string;
}

interface Manufacturer {
  id: string;
  name: string;
}

interface DroneModel {
  id: string;
  manufacturerId: string;
  modelName: string;
}

@Component({
  selector: 'app-drone-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './drone-form.component.html',
  styleUrl: './drone-form.component.scss'
})
export class DroneFormComponent implements OnInit {
  droneForm: FormGroup;
  loading = false;
  loadingDrone = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  droneId: string | null = null;

  // Dropdown options from API
  manufacturers: Manufacturer[] = [];
  allModels: DroneModel[] = [];
  availableModels: DroneModel[] = [];

  statuses = [
    { value: 'available', label: 'Available' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.droneForm = this.fb.group({
      name: ['', Validators.required],
      manufacturerId: ['', Validators.required],
      modelId: ['', Validators.required],
      serialNumber: ['', Validators.required],
      packagingSerialNumber: [''],
      version: [''],
      status: ['available', Validators.required],
      inventoryNote: [''],
      // Registration fields
      nNumber: [''],
      nNumberActiveDate: [''],
      remoteId: [''],
      remoteBroadcastModule: [''],
    });

    // Watch manufacturer changes to update available models
    this.droneForm.get('manufacturerId')?.valueChanges.subscribe(manufacturerId => {
      this.updateAvailableModels(manufacturerId);
    });
  }

  ngOnInit(): void {
    // Check if we're in edit mode
    this.droneId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.droneId && this.droneId !== 'create';

    // Load manufacturers and models first
    this.loadManufacturersAndModels().then(() => {
      if (this.isEditMode) {
        this.loadDrone();
      }
    });
  }

  async loadManufacturersAndModels(): Promise<void> {
    try {
      // Load manufacturers
      const mfrResponse = await firstValueFrom(
        this.http.get<{ success: boolean; data: Manufacturer[] }>(
          `${environment.apiUrl}/admin/drone-manufacturers`
        )
      );
      if (mfrResponse.success && mfrResponse.data) {
        this.manufacturers = mfrResponse.data;
      }

      // Load all models
      const modelResponse = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneModel[] }>(
          `${environment.apiUrl}/admin/drone-models`
        )
      );
      if (modelResponse.success && modelResponse.data) {
        this.allModels = modelResponse.data;
      }
    } catch (error) {
      console.error('Failed to load manufacturers/models:', error);
      this.errorMessage = 'Failed to load manufacturer and model options';
    }
  }

  updateAvailableModels(manufacturerId: string): void {
    if (!manufacturerId) {
      this.availableModels = [];
      return;
    }

    this.availableModels = this.allModels.filter(m => m.manufacturerId === manufacturerId);

    // Reset model if current model isn't in the new list
    const currentModelId = this.droneForm.get('modelId')?.value;
    if (currentModelId && !this.availableModels.find(m => m.id === currentModelId)) {
      this.droneForm.get('modelId')?.setValue('');
    }
  }

  async loadDrone(): Promise<void> {
    this.loadingDrone = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneInventory }>(
          `${environment.apiUrl}/admin/drones/${this.droneId}`
        )
      );

      if (response.success && response.data) {
        const drone = response.data;

        // Update available models for this manufacturer first
        this.updateAvailableModels(drone.manufacturerId);

        // Format dates for input fields
        const nNumberActiveDate = drone.nNumberActiveDate
          ? new Date(drone.nNumberActiveDate).toISOString().split('T')[0]
          : '';

        // Patch form values
        this.droneForm.patchValue({
          name: drone.name,
          manufacturerId: drone.manufacturerId,
          modelId: drone.modelId,
          serialNumber: drone.serialNumber,
          packagingSerialNumber: drone.packagingSerialNumber || '',
          version: drone.version || '',
          status: drone.status,
          inventoryNote: drone.inventoryNote || '',
          nNumber: drone.nNumber || '',
          nNumberActiveDate: nNumberActiveDate,
          remoteId: drone.remoteId || '',
          remoteBroadcastModule: drone.remoteBroadcastModule || '',
        });
      }
    } catch (error) {
      console.error('Failed to load drone:', error);
      this.errorMessage = 'Failed to load drone details';
    } finally {
      this.loadingDrone = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.droneForm.invalid) {
      this.droneForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formData = this.droneForm.value;

      // Build the payload
      const payload: any = {
        name: formData.name,
        manufacturerId: formData.manufacturerId,
        modelId: formData.modelId,
        serialNumber: formData.serialNumber,
        packagingSerialNumber: formData.packagingSerialNumber || null,
        version: formData.version || null,
        status: formData.status,
        inventoryNote: formData.inventoryNote || null,
        nNumber: formData.nNumber || null,
        nNumberActiveDate: formData.nNumberActiveDate || null,
        remoteId: formData.remoteId || null,
        remoteBroadcastModule: formData.remoteBroadcastModule || null,
      };

      if (this.isEditMode) {
        // Update existing drone
        const response = await firstValueFrom(
          this.http.put<{ success: boolean; data: DroneInventory }>(
            `${environment.apiUrl}/admin/drones/${this.droneId}`,
            payload
          )
        );

        if (response.success) {
          this.successMessage = 'Drone updated successfully';

          // Navigate back to detail page after short delay
          setTimeout(() => {
            this.router.navigate(['/drones', this.droneId, 'details']);
          }, 1500);
        }
      } else {
        // Create new drone
        const response = await firstValueFrom(
          this.http.post<{ success: boolean; data: DroneInventory }>(
            `${environment.apiUrl}/admin/drones`,
            payload
          )
        );

        if (response.success && response.data) {
          // Get manufacturer and model names for success page
          const manufacturer = this.manufacturers.find(m => m.id === formData.manufacturerId);
          const model = this.availableModels.find(m => m.id === formData.modelId);

          // Navigate to success page
          this.router.navigate(['/success'], {
            state: {
              entities: [
                { type: 'drone', action: 'created', name: formData.name },
              ],
              message: 'Drone registered successfully',
              details: {
                'Manufacturer': manufacturer?.name || 'Unknown',
                'Model': model?.modelName || 'Unknown',
                'Serial Number': formData.serialNumber,
                'N-Number': formData.nNumber || 'Not registered',
                'Status': this.getStatusLabel(formData.status),
                'Inventory Note': (formData.inventoryNote || '').trim() || '—'
              },
              createAnotherRoute: '/drones/create'
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Save drone error:', error);
      this.errorMessage = error.error?.error || error.error?.message || error.message || (this.isEditMode
        ? 'Failed to update drone. Please try again.'
        : 'Failed to create drone. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    if (this.isEditMode && this.droneId) {
      this.router.navigate(['/drones', this.droneId, 'details']);
    } else {
      this.router.navigate(['/drones']);
    }
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Drone' : 'Add New Drone';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Drone';
  }

  /**
   * Get display label for status
   */
  private getStatusLabel(status: string): string {
    const found = this.statuses.find(s => s.value === status);
    return found?.label || status;
  }
}
